import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Add,
  Button,
  DatePicker,
  Delete,
  FileDropZone,
  FilePresent,
  OrDivider,
  PageTitleAndSubTitle,
  TextField,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { removeEmptyFields } from '../../../core-utils/helper-utils/riskRegisterHelper';
import { financialForensicsConstants } from '../../../core-utils/messages/financialForensicsConstants';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import {
  getEmptyVendorModalState,
  VendorModalType,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  emptyService,
  emptyVendor,
  Service,
  Vendor,
} from '../../../pages/FinancialForensicsPage/types/FixedCost.types';

export interface AddVendorModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<VendorModalType>>;
   
  handleSubmit: (data: any) => void;
   
  handleDelete: (vendorID: string) => void;
  handleOpenSnackBar: any;
  startDateDefault?: string;
  endDateDefault?: string;
  dataFromProps?: Vendor;
}

const AddVendorModal: React.FC<AddVendorModalProps> = observer(
  ({
    open,
    setOpen,
    handleSubmit,
    handleDelete,
    startDateDefault,
    endDateDefault,
    dataFromProps,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '676px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: { padding: '0px' },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        alignItems: 'start',
        margin: '0px 16px 20px 0px',
      },
      service: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 20px 15px 20px',
        height: 'fit-content',
      },
      content: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '15px',
        overflowY: 'scroll',
        height: '588px',
      },
    });

    const { classes } = useStyles();

    const [data, setData] = useState<Vendor>(() =>
      emptyVendor(startDateDefault, endDateDefault),
    );
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const isCommonFieldsFilled =
      !!data.nameOfVendor &&
      data.services.some(
        (service) =>
          service.startDate &&
          service.endDate &&
          service.nameOfService &&
          service.payer &&
          service.modeOfPayment &&
          service.applicable &&
          service.unitOfFees &&
          service.value,
      );

    const clearData = () => {
      setData(emptyVendor(startDateDefault, endDateDefault));
      setUploadedFiles([]);
    };

    const handleClose = () => {
      clearData();
      setOpen(getEmptyVendorModalState());
    };

    const handleSaveOrSubmit = () => {
      const cleanedData = removeEmptyFields(data);
      handleSubmit({ ...cleanedData, files: uploadedFiles });
      clearData();
      setOpen(getEmptyVendorModalState());
    };

    const handleAddNewService = () => {
      setData((prev) => ({
        ...prev,
        services: [
          ...prev.services,
          emptyService(startDateDefault, endDateDefault),
        ],
      }));
    };

    const handleDeleteService = (index: number) => {
      setData((prev) => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index),
      }));
    };

    const updateService = (
      index: number,
      field: keyof Service,
      value: string,
    ) => {
      setData((prev) => ({
        ...prev,
        services: prev.services.map((service, i) =>
          i === index ? { ...service, [field]: value } : service,
        ),
      }));
    };

    useEffect(() => {
      if (!dataFromProps) {
        return;
      }
      setData(dataFromProps);
    }, [dataFromProps]);

    return (
      <Modal open={open} onClose={handleClose} className={classes.modal}>
        <Paper className={classes.paper}>
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '30px',
            }}
          >
            <PageTitleAndSubTitle
              title={financialForensicsConstants.addVendor}
              subTitle={''}
              subTitleStyles={{ width: '0px' }}
            />
            <SvgClose
              style={{ cursor: 'pointer' }}
              color={patientAccessTheme.palette.status.icon}
              onClick={handleClose}
            />
          </Grid>

          <Grid className={`${classes.scroll} ${classes.content}`}>
            <Grid className={classes.service} style={{ marginBottom: '30px' }}>
              <ReportCreateNewLabelField
                label={financialForensicsConstants.vendorName}
                component={
                  <TextField
                    width="555px"
                    height="46px"
                    placeholder={financialForensicsConstants.vendorName}
                    onChange={(event: any) =>
                      setData((prev) => ({
                        ...prev,
                        nameOfVendor: event.target.value,
                      }))
                    }
                    value={data.nameOfVendor}
                    placeholderStyle={{ fontSize: '16px' }}
                    error={!data.nameOfVendor}
                  />
                }
              />
            </Grid>

            {/* Services */}
            {data.services.map((service, index) => (
              <React.Fragment key={service.serviceID || index}>
                {/* Service Header */}
                <Grid
                  container
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    textColor={patientAccessTheme.palette.text.lowemphasis}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType="medium"
                  >
                    {`${financialForensicsConstants.service_type} ${index + 1}`}
                  </Typography>
                  <Grid
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginRight: '20px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleDeleteService(index)}
                  >
                    <Typography
                      textColor={patientAccessTheme.palette.text.lowemphasis}
                      fontSize="12px"
                      lineHeight="12px"
                      fontType="medium"
                    >
                      {financialForensicsConstants.delete}
                    </Typography>
                    <Delete />
                  </Grid>
                </Grid>

                <Grid className={`${classes.flex} ${classes.service}`}>
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.start_date}
                    component={
                      <DatePicker
                        width="270px"
                        handleChange={(value) =>
                          updateService(
                            index,
                            'startDate',
                            moment(value).format('YYYY-MM-DD'),
                          )
                        }
                        selectedDate={
                          service.startDate
                            ? moment(service.startDate)
                            : startDateDefault
                        }
                        format="yyyy-MM-dd"
                        placeholder={financialForensicsConstants.date_format}
                        error={!service.startDate}
                        views={['year', 'month']}
                        minDate={undefined}
                        maxDate={undefined}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.end_date}
                    component={
                      <DatePicker
                        width="270px"
                        handleChange={(value) =>
                          updateService(
                            index,
                            'endDate',
                            moment(value).format('YYYY-MM-DD'),
                          )
                        }
                        selectedDate={
                          service.endDate
                            ? moment(service.endDate)
                            : endDateDefault
                        }
                        format="yyyy-MM-dd"
                        placeholder={financialForensicsConstants.date_format}
                        error={!service.endDate}
                        views={['year', 'month']}
                        minDate={undefined}
                        maxDate={undefined}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.service_name}
                    component={
                      <TextField
                        width="270px"
                        height="36px"
                        placeholder={financialForensicsConstants.service_name}
                        onChange={(event: any) =>
                          updateService(
                            index,
                            'nameOfService',
                            event.target.value,
                          )
                        }
                        value={service.nameOfService}
                        placeholderStyle={{ fontSize: '16px' }}
                        error={!service.nameOfService}
                      />
                    }
                    marginBottom="12px"
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.fee_contribution}
                    component={
                      <Dropdown
                        label={financialForensicsConstants.fee_contribution}
                        dropDownOptions={[
                          'Plan Contribution',
                          'Member Contribution',
                        ]}
                        dropDownValues={[
                          'Plan Contribution',
                          'Member Contribution',
                        ]}
                        value={service.payer}
                        setValue={(value: string) =>
                          updateService(index, 'payer', value)
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '270px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          updateService(index, 'payer', event.target.value)
                        }
                        error={!service.payer}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.payment_mode}
                    component={
                      <Dropdown
                        label={financialForensicsConstants.payment_mode}
                        dropDownOptions={['Through Claims Administrator']}
                        dropDownValues={['Through Claims Administrator']}
                        value={service.modeOfPayment}
                        setValue={(value: string) =>
                          updateService(index, 'modeOfPayment', value)
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '270px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          updateService(
                            index,
                            'modeOfPayment',
                            event.target.value,
                          )
                        }
                        error={!service.modeOfPayment}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.applicable}
                    component={
                      <Dropdown
                        label={financialForensicsConstants.applicable}
                        dropDownOptions={
                          financialForensicsConstants.APPLICABLE_OPTIONS
                        }
                        dropDownValues={
                          financialForensicsConstants.APPLICABLE_OPTIONS
                        }
                        value={service.applicable}
                        setValue={(value: string) =>
                          updateService(index, 'applicable', value)
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '270px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          updateService(index, 'applicable', event.target.value)
                        }
                        error={!service.applicable}
                      />
                    }
                  />
                  <ReportCreateNewLabelField
                    label={financialForensicsConstants.fee_unit}
                    component={
                      <Dropdown
                        label={financialForensicsConstants.fee_unit}
                        dropDownOptions={financialForensicsConstants.UNITS}
                        dropDownValues={financialForensicsConstants.UNITS}
                        value={service.unitOfFees}
                        setValue={(value: string) =>
                          updateService(index, 'unitOfFees', value)
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '555px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          updateService(index, 'unitOfFees', event.target.value)
                        }
                        error={!service.unitOfFees}
                      />
                    }
                  />
                  <TextField
                    width="555px"
                    height="36px"
                    placeholder={financialForensicsConstants.fee_placeholder}
                    onChange={(event: any) =>
                      updateService(index, 'value', event.target.value)
                    }
                    value={service.value}
                    placeholderStyle={{ fontSize: '16px' }}
                    error={!service.value}
                  />
                </Grid>
              </React.Fragment>
            ))}

            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.primary[400]}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.add_new_service}
                </Typography>
              }
              startIcon={
                <Add
                  stroke={patientAccessTheme.palette.primary[400]}
                  fill={patientAccessTheme.palette.primary[400]}
                />
              }
              variant="outlined"
              height="32px"
              width="595px"
              borderRadius="4px"
              className={classes.button}
              onClick={handleAddNewService}
            />

            <OrDivider containerStyles={{ width: '590px' }} />

            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="12px"
              lineHeight="12px"
              fontType="medium"
            >
              {financialForensicsConstants.upload}
            </Typography>
            <FileDropZone
              filesSupported={['.pdf', '.doc', '.docx']}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              isMultiple={true}
              dragDropAreaStyles={{ marginRight: '20px' }}
            />
            {uploadedFiles.map((file, index) => (
              <UploadedDocument
                key={index}
                width="592px"
                name={file.name}
                truncateWidth="420px"
                size={formatFileSize(file.size)}
                startIcon={
                  <FilePresent
                    stroke={patientAccessTheme.palette.primary[500]}
                    fill={patientAccessTheme.palette.primary[500]}
                  />
                }
                handleDelete={() =>
                   
                  setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
                }
              />
            ))}
          </Grid>

          <Grid
            style={{
              display: 'flex',
              justifyContent: 'right',
              margin: '0px 40px 40px 0px',
              position: 'absolute',
              right: '0px',
              bottom: '0px',
            }}
          >
            <Button
              label={
                <>
                  <Typography
                    textColor={patientAccessTheme.palette.status.error}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType="medium"
                  >
                    {financialForensicsConstants.delete_vendor}
                  </Typography>
                  <Delete stroke={patientAccessTheme.palette.status.error} />
                </>
              }
              variant="text"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={() => {
                handleDelete(data.vendorID);
                clearData();
                setOpen(getEmptyVendorModalState());
              }}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.primary.main}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.Save_update}
                </Typography>
              }
              variant="outlined"
              height="36px"
              width="180px"
              styleProps={{ marginRight: '25px' }}
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
            />
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="12px"
                  lineHeight="12px"
                  fontType="medium"
                >
                  {financialForensicsConstants.submit}
                </Typography>
              }
              variant="contained"
              color="primary"
              height="36px"
              width="180px"
              borderRadius="4px"
              className={classes.button}
              onClick={handleSaveOrSubmit}
              disabled={!isCommonFieldsFilled}
            />
          </Grid>
        </Paper>
      </Modal>
    );
  },
);

export default AddVendorModal;
